import { defineMessages, type MessageDescriptor } from 'react-intl';
import type {
  MusicVideoCutIn,
  MusicVideoEndCardLine,
  MusicVideoGrade,
  MusicVideoLyricStyle,
  MusicVideoMove,
} from 'fym-shared/src/music24/musicVideoLook';

/** The copy of the Text, Lyrics and Look panels and of the inspector (design music-video b1, c3, d1 to d4, h3, f). */
export const panelMessages = defineMessages({
  // Text (d2)
  textTitle: { id: 'app.sml.musicVideo.text.title', defaultMessage: 'Text' },
  titleItem: { id: 'app.sml.musicVideo.text.titleItem', defaultMessage: 'Title at the start' },
  nameItem: { id: 'app.sml.musicVideo.text.nameItem', defaultMessage: 'Name on screen' },
  endCardItem: { id: 'app.sml.musicVideo.text.endCardItem', defaultMessage: 'End card' },
  cornerItem: { id: 'app.sml.musicVideo.text.cornerItem', defaultMessage: 'Song info in the corner' },
  spanFacts: { id: 'app.sml.musicVideo.text.spanFacts', defaultMessage: '{from}–{to} · {text}' },
  endFacts: { id: 'app.sml.musicVideo.text.endFacts', defaultMessage: 'last {seconds} s · cover + SmartLink' },
  endFactsShort: {
    id: 'app.sml.musicVideo.text.endFactsShort',
    defaultMessage: 'the video is too short for an end card',
  },
  cornerFacts: { id: 'app.sml.musicVideo.text.cornerFacts', defaultMessage: 'the whole video · off = clean' },
  addText: { id: 'app.sml.musicVideo.text.add', defaultMessage: '+ Add text at {time}' },
  newText: { id: 'app.sml.musicVideo.text.new', defaultMessage: 'New text' },
  customFacts: { id: 'app.sml.musicVideo.text.customFacts', defaultMessage: '{from}–{to}' },
  endCardEyebrow: { id: 'app.sml.musicVideo.text.endCardEyebrow', defaultMessage: 'End card' },
  endLine: { id: 'app.sml.musicVideo.text.endLine', defaultMessage: 'Line' },
  endLink: { id: 'app.sml.musicVideo.text.endLink', defaultMessage: 'Link' },
  endLinkSong: { id: 'app.sml.musicVideo.text.endLinkSong', defaultMessage: 'SmartLink of the song' },
  endLinkNone: { id: 'app.sml.musicVideo.text.endLinkNone', defaultMessage: 'No SmartLink yet' },
  textHint: {
    id: 'app.sml.musicVideo.text.hint',
    defaultMessage:
      'The fonts and colours come from the release theme. Before the release day the line says “Pre-save now”.',
  },
  textField: { id: 'app.sml.musicVideo.text.field', defaultMessage: 'Text' },
  textFieldSongTitle: { id: 'app.sml.musicVideo.text.fieldSongTitle', defaultMessage: 'The song title' },
  textFieldArtist: { id: 'app.sml.musicVideo.text.fieldArtist', defaultMessage: 'The artist name' },
  show: { id: 'app.sml.musicVideo.text.show', defaultMessage: 'Show it' },
  starts: { id: 'app.sml.musicVideo.text.starts', defaultMessage: 'Starts' },
  ends: { id: 'app.sml.musicVideo.text.ends', defaultMessage: 'Ends' },
  remove: { id: 'app.sml.musicVideo.text.remove', defaultMessage: 'Remove' },
  setToPlayHead: { id: 'app.sml.musicVideo.text.setToPlayHead', defaultMessage: 'Set to the play head' },

  // Lyrics (d1)
  lyricsTitle: { id: 'app.sml.musicVideo.lyrics.title', defaultMessage: 'Lyrics' },
  lyricsShow: { id: 'app.sml.musicVideo.lyrics.show', defaultMessage: 'Show the lyrics' },
  lyricsFacts: {
    id: 'app.sml.musicVideo.lyrics.facts',
    defaultMessage: '{status} · {count, plural, one {# line} other {# lines}}',
  },
  statusSynced: { id: 'app.sml.musicVideo.lyrics.status.synced', defaultMessage: 'synced' },
  statusSyncing: { id: 'app.sml.musicVideo.lyrics.status.syncing', defaultMessage: 'syncing' },
  statusFailed: { id: 'app.sml.musicVideo.lyrics.status.failed', defaultMessage: 'not synced' },
  statusNone: { id: 'app.sml.musicVideo.lyrics.status.none', defaultMessage: 'no timing' },
  where: { id: 'app.sml.musicVideo.lyrics.where', defaultMessage: 'Where' },
  bottom: { id: 'app.sml.musicVideo.lyrics.bottom', defaultMessage: 'Bottom' },
  middle: { id: 'app.sml.musicVideo.lyrics.middle', defaultMessage: 'Middle' },
  when: { id: 'app.sml.musicVideo.lyrics.when', defaultMessage: 'When' },
  whenAll: { id: 'app.sml.musicVideo.lyrics.whenAll', defaultMessage: 'All' },
  whenChoruses: { id: 'app.sml.musicVideo.lyrics.whenChoruses', defaultMessage: 'Choruses' },
  linesHeading: { id: 'app.sml.musicVideo.lyrics.linesHeading', defaultMessage: 'Lines' },
  linesHint: { id: 'app.sml.musicVideo.lyrics.linesHint', defaultMessage: 'click a line to jump there' },
  lineSynced: { id: 'app.sml.musicVideo.lyrics.lineSynced', defaultMessage: 'synced' },
  lineNoTime: { id: 'app.sml.musicVideo.lyrics.lineNoTime', defaultMessage: 'no time' },
  editTiming: { id: 'app.sml.musicVideo.lyrics.editTiming', defaultMessage: 'Edit the timing' },
  lyricsHint: {
    id: 'app.sml.musicVideo.lyrics.hint',
    defaultMessage:
      'The words and the timing belong to the song (Catalog). A change there changes them for every asset.',
  },
  noLyrics: {
    id: 'app.sml.musicVideo.lyrics.none',
    defaultMessage: 'The song has no lyrics yet. The video works with no captions.',
  },
  addLyrics: { id: 'app.sml.musicVideo.lyrics.add', defaultMessage: 'Add the lyrics' },
  syncingNote: {
    id: 'app.sml.musicVideo.lyrics.syncingNote',
    defaultMessage: 'We make the timing of the lyrics now. The captions show when it is ready.',
  },
  sample: { id: 'app.sml.musicVideo.lyrics.sample', defaultMessage: 'keeps our names' },

  // Look (d3)
  lookTitle: { id: 'app.sml.musicVideo.look.title', defaultMessage: 'Look' },
  colour: { id: 'app.sml.musicVideo.look.colour', defaultMessage: 'Colour' },
  gradeHint: {
    id: 'app.sml.musicVideo.look.gradeHint',
    defaultMessage: 'One colour over all clips, so the bank and your own footage look like one shoot.',
  },
  onTop: { id: 'app.sml.musicVideo.look.onTop', defaultMessage: 'On top' },
  grain: { id: 'app.sml.musicVideo.look.grain', defaultMessage: 'Film grain' },
  grainFacts: { id: 'app.sml.musicVideo.look.grainFacts', defaultMessage: 'light' },
  vignette: { id: 'app.sml.musicVideo.look.vignette', defaultMessage: 'Dark corners' },
  vignetteFacts: { id: 'app.sml.musicVideo.look.vignetteFacts', defaultMessage: 'vignette' },
  flash: { id: 'app.sml.musicVideo.look.flash', defaultMessage: 'Flash on the drop' },
  flashFacts: { id: 'app.sml.musicVideo.look.flashFacts', defaultMessage: 'at the first beat of each chorus' },
  leaks: { id: 'app.sml.musicVideo.look.leaks', defaultMessage: 'Light leaks' },
  leaksFacts: { id: 'app.sml.musicVideo.look.leaksFacts', defaultMessage: 'on cuts between sections' },
  cutSpeed: { id: 'app.sml.musicVideo.look.cutSpeed', defaultMessage: 'Cut speed of the whole video' },
  calm: { id: 'app.sml.musicVideo.look.calm', defaultMessage: 'Calm' },
  fast: { id: 'app.sml.musicVideo.look.fast', defaultMessage: 'Fast' },
  cutSpeedHint: {
    id: 'app.sml.musicVideo.look.cutSpeedHint',
    defaultMessage: 'A new cut speed recuts the sections and keeps your kept clips.',
  },
  cutSpeedConfirmTitle: {
    id: 'app.sml.musicVideo.look.cutSpeedConfirmTitle',
    defaultMessage: 'Recut the whole video?',
  },
  cutSpeedConfirmText: {
    id: 'app.sml.musicVideo.look.cutSpeedConfirmText',
    defaultMessage: 'A new cut speed recuts every section with the AI. Your kept clips stay. It costs no credits.',
  },
  cutSpeedConfirm: { id: 'app.sml.musicVideo.look.cutSpeedConfirm', defaultMessage: 'Recut' },
  cutSpeedCancel: { id: 'app.sml.musicVideo.look.cutSpeedCancel', defaultMessage: 'Keep the cut' },

  // The clip inspector (b1, c3, h3)
  clipHeading: { id: 'app.sml.musicVideo.clip.heading', defaultMessage: 'Clip {number}' },
  clipSub: {
    id: 'app.sml.musicVideo.clip.sub',
    defaultMessage: '{section} · {from}–{to} · {beats, plural, one {# beat} other {# beats}}',
  },
  clipSubNoSection: { id: 'app.sml.musicVideo.clip.subNoSection', defaultMessage: '{from}–{to}' },
  sourceBank: { id: 'app.sml.musicVideo.clip.source.bank', defaultMessage: 'Bank' },
  sourceUpload: { id: 'app.sml.musicVideo.clip.source.upload', defaultMessage: 'Your clip' },
  sourceAi: { id: 'app.sml.musicVideo.clip.source.ai', defaultMessage: 'AI clip · yours' },
  sourceSongVideo: { id: 'app.sml.musicVideo.clip.source.songVideo', defaultMessage: 'The music video' },
  sourceSize: { id: 'app.sml.musicVideo.clip.source.size', defaultMessage: '{source} · {width} × {height}' },
  unknownClip: {
    id: 'app.sml.musicVideo.clip.unknown',
    defaultMessage: 'We still load this clip. It shows when the list is here.',
  },
  partOfClip: { id: 'app.sml.musicVideo.clip.part', defaultMessage: 'Part of the clip' },
  partFrom: { id: 'app.sml.musicVideo.clip.partFrom', defaultMessage: 'from {time}' },
  partOf: { id: 'app.sml.musicVideo.clip.partOf', defaultMessage: '{length} s of {total} s' },
  partEarlier: { id: 'app.sml.musicVideo.clip.partEarlier', defaultMessage: 'Earlier part' },
  partLater: { id: 'app.sml.musicVideo.clip.partLater', defaultMessage: 'Later part' },
  speed: { id: 'app.sml.musicVideo.clip.speed', defaultMessage: 'Speed' },
  speedHalf: { id: 'app.sml.musicVideo.clip.speedHalf', defaultMessage: '½×' },
  speedOne: { id: 'app.sml.musicVideo.clip.speedOne', defaultMessage: '1×' },
  speedTwo: { id: 'app.sml.musicVideo.clip.speedTwo', defaultMessage: '2×' },
  move: { id: 'app.sml.musicVideo.clip.move', defaultMessage: 'Move' },
  cutIn: { id: 'app.sml.musicVideo.clip.cutIn', defaultMessage: 'Cut in' },
  colourOfVideo: { id: 'app.sml.musicVideo.clip.colourOfVideo', defaultMessage: 'Colour' },
  colourValue: { id: 'app.sml.musicVideo.clip.colourValue', defaultMessage: 'Look of the video · {grade}' },
  fit: { id: 'app.sml.musicVideo.clip.fit', defaultMessage: 'Fit the 16:9 frame' },
  fitFill: { id: 'app.sml.musicVideo.clip.fitFill', defaultMessage: 'Fill' },
  fitBlur: { id: 'app.sml.musicVideo.clip.fitBlur', defaultMessage: 'Fit with blur' },
  fitHint: {
    id: 'app.sml.musicVideo.clip.fitHint',
    defaultMessage: 'Fill shows a part of the clip. Fit with blur shows all of it, with a blurred copy behind.',
  },
  cropHint: { id: 'app.sml.musicVideo.clip.cropHint', defaultMessage: 'drag to pick the part' },
  similar: { id: 'app.sml.musicVideo.clip.similar', defaultMessage: 'Replace with a similar clip' },
  keep: { id: 'app.sml.musicVideo.clip.keep', defaultMessage: 'Keep on recut' },
  kept: { id: 'app.sml.musicVideo.clip.kept', defaultMessage: 'Kept on recut' },
  split: { id: 'app.sml.musicVideo.clip.split', defaultMessage: 'Split at beat' },
  clipRemove: { id: 'app.sml.musicVideo.clip.remove', defaultMessage: 'Remove (the clip before fills it)' },
  keepHint: {
    id: 'app.sml.musicVideo.clip.keepHint',
    defaultMessage:
      '“Keep on recut” protects this clip when you or the AI recut a section. With nothing selected, this panel shows the video settings.',
  },
  ownKeepHint: {
    id: 'app.sml.musicVideo.clip.ownKeepHint',
    defaultMessage: 'Your own clips are kept on recut by default.',
  },
  variation: {
    id: 'app.sml.musicVideo.clip.variation',
    defaultMessage: 'Make a variation · {credits, plural, one {# credit} other {# credits}}',
  },
  aiClipHint: {
    id: 'app.sml.musicVideo.clip.aiHint',
    defaultMessage:
      'Your AI clips stay yours: use them again in this video, in other songs, in AI reels and in the Canvas loop. They are kept on recut.',
  },
  aiMadeOn: { id: 'app.sml.musicVideo.clip.aiMadeOn', defaultMessage: 'AI clip · yours · made {date}' },
  noSimilar: {
    id: 'app.sml.musicVideo.clip.noSimilar',
    defaultMessage: 'No other clip of the bank fits this section yet.',
  },

  // A text or a lyric line (inspector)
  lyricHeading: { id: 'app.sml.musicVideo.lyric.heading', defaultMessage: 'Line {number}' },
  lyricTime: { id: 'app.sml.musicVideo.lyric.time', defaultMessage: '{from}–{to}' },
  lyricNoTime: { id: 'app.sml.musicVideo.lyric.noTime', defaultMessage: 'This line has no time yet.' },

  // The video settings (d4)
  videoHeading: { id: 'app.sml.musicVideo.video.heading', defaultMessage: 'Video' },
  videoSub: { id: 'app.sml.musicVideo.video.sub', defaultMessage: '{title} · cut {number}' },
  sound: { id: 'app.sml.musicVideo.video.sound', defaultMessage: 'Sound' },
  soundValue: { id: 'app.sml.musicVideo.video.soundValue', defaultMessage: 'Master' },
  locked: { id: 'app.sml.musicVideo.video.locked', defaultMessage: 'locked' },
  videoStarts: { id: 'app.sml.musicVideo.video.starts', defaultMessage: 'Starts' },
  videoEnds: { id: 'app.sml.musicVideo.video.ends', defaultMessage: 'Ends' },
  songStart: { id: 'app.sml.musicVideo.video.songStart', defaultMessage: 'song start' },
  songEnd: { id: 'app.sml.musicVideo.video.songEnd', defaultMessage: 'song end' },
  fadeIn: { id: 'app.sml.musicVideo.video.fadeIn', defaultMessage: 'Fade in' },
  fadeOut: { id: 'app.sml.musicVideo.video.fadeOut', defaultMessage: 'Fade out' },
  off: { id: 'app.sml.musicVideo.video.off', defaultMessage: 'Off' },
  fromBlack: { id: 'app.sml.musicVideo.video.fromBlack', defaultMessage: 'From black' },
  toBlack: { id: 'app.sml.musicVideo.video.toBlack', defaultMessage: 'To black' },
  footageEyebrow: { id: 'app.sml.musicVideo.video.footageEyebrow', defaultMessage: 'Footage in this cut' },
  bankCount: { id: 'app.sml.musicVideo.video.bankCount', defaultMessage: 'Clip bank' },
  ownCount: { id: 'app.sml.musicVideo.video.ownCount', defaultMessage: 'Your footage' },
  countClips: {
    id: 'app.sml.musicVideo.video.countClips',
    defaultMessage: '{count, plural, one {# clip} other {# clips}}',
  },
  cutsEyebrow: { id: 'app.sml.musicVideo.video.cutsEyebrow', defaultMessage: 'Cuts' },
  cutRow: { id: 'app.sml.musicVideo.video.cutRow', defaultMessage: 'Cut {number}' },

  // The phone (f1, f2)
  phoneSections: { id: 'app.sml.musicVideo.phone.sections', defaultMessage: 'Sections' },
  phoneSectionFacts: {
    id: 'app.sml.musicVideo.phone.sectionFacts',
    defaultMessage: '{time} · {mood} · {count, plural, one {# clip} other {# clips}}',
  },
  phoneSectionFactsNoMood: {
    id: 'app.sml.musicVideo.phone.sectionFactsNoMood',
    defaultMessage: '{time} · {count, plural, one {# clip} other {# clips}}',
  },
  phoneNoCredits: { id: 'app.sml.musicVideo.phone.noCredits', defaultMessage: 'No credits to watch' },
  phoneSwap: { id: 'app.sml.musicVideo.phone.swap', defaultMessage: 'Swap this clip' },
  phoneRecut: { id: 'app.sml.musicVideo.phone.recut', defaultMessage: 'Recut the section' },
  phoneClose: { id: 'app.sml.musicVideo.phone.close', defaultMessage: 'Close' },
  phonePick: { id: 'app.sml.musicVideo.phone.pick', defaultMessage: 'Pick a clip' },
  phoneClipLength: { id: 'app.sml.musicVideo.phone.clipLength', defaultMessage: '{seconds} s' },
  phoneSavedLine: { id: 'app.sml.musicVideo.phone.savedLine', defaultMessage: 'music video · cut {number} · {state}' },
});

export const gradeMessages: Readonly<Record<MusicVideoGrade, MessageDescriptor>> = defineMessages({
  film: { id: 'app.sml.musicVideo.look.grade.film', defaultMessage: 'Film' },
  noir: { id: 'app.sml.musicVideo.look.grade.noir', defaultMessage: 'Noir' },
  neon: { id: 'app.sml.musicVideo.look.grade.neon', defaultMessage: 'Neon' },
  warm: { id: 'app.sml.musicVideo.look.grade.warm', defaultMessage: 'Warm' },
  cold: { id: 'app.sml.musicVideo.look.grade.cold', defaultMessage: 'Cold' },
  none: { id: 'app.sml.musicVideo.look.grade.none', defaultMessage: 'None' },
});

export const moveMessages: Readonly<Record<MusicVideoMove, MessageDescriptor>> = defineMessages({
  still: { id: 'app.sml.musicVideo.clip.move.still', defaultMessage: 'Still' },
  pushIn: { id: 'app.sml.musicVideo.clip.move.pushIn', defaultMessage: 'Slow push in' },
  pullOut: { id: 'app.sml.musicVideo.clip.move.pullOut', defaultMessage: 'Slow pull out' },
  panLeft: { id: 'app.sml.musicVideo.clip.move.panLeft', defaultMessage: 'Pan left' },
  panRight: { id: 'app.sml.musicVideo.clip.move.panRight', defaultMessage: 'Pan right' },
  followFace: { id: 'app.sml.musicVideo.clip.move.followFace', defaultMessage: 'Follow the face' },
});

export const cutInMessages: Readonly<Record<MusicVideoCutIn, MessageDescriptor>> = defineMessages({
  cut: { id: 'app.sml.musicVideo.clip.cutIn.cut', defaultMessage: 'Cut' },
  flash: { id: 'app.sml.musicVideo.clip.cutIn.flash', defaultMessage: 'Flash' },
  fade: { id: 'app.sml.musicVideo.clip.cutIn.fade', defaultMessage: 'Fade' },
  whip: { id: 'app.sml.musicVideo.clip.cutIn.whip', defaultMessage: 'Whip' },
});

export const lyricStyleMessages: Readonly<Record<MusicVideoLyricStyle, MessageDescriptor>> = defineMessages({
  clean: { id: 'app.sml.musicVideo.lyrics.style.clean', defaultMessage: 'Clean' },
  caps: { id: 'app.sml.musicVideo.lyrics.style.caps', defaultMessage: 'Big caps' },
  serif: { id: 'app.sml.musicVideo.lyrics.style.serif', defaultMessage: 'Serif' },
  word: { id: 'app.sml.musicVideo.lyrics.style.word', defaultMessage: 'One word' },
});

export const endCardLineMessages: Readonly<Record<MusicVideoEndCardLine, MessageDescriptor>> = defineMessages({
  auto: { id: 'app.sml.musicVideo.text.line.auto', defaultMessage: 'By the release day' },
  outNow: { id: 'app.sml.musicVideo.text.line.outNow', defaultMessage: 'Out now' },
  preSave: { id: 'app.sml.musicVideo.text.line.preSave', defaultMessage: 'Pre-save now' },
  listen: { id: 'app.sml.musicVideo.text.line.listen', defaultMessage: 'Listen now' },
});
